// Amix dibujado (D52): un SVG que se anima cuadro a cuadro con requestAnimationFrame y escribe directo en el DOM (sin
// re-render de React). Toma el color de la paleta elegida en Apariencia (--h). Con "reducir movimiento" muestra la cara
// del estado sin moverse.
import { useEffect, useId, useRef } from 'react';

import { BASE, CUERPO, type EstadoAmix, type Gesto, gestoEn, GUIONES, NEUTRO, pose } from '../lib/personaje/cara';

// Cursor compartido por todos los Amix de la pantalla.
const cursor = { x: -9999, y: -9999, en: 0 };
let escuchando = false;
function seguirCursor() {
  if (escuchando || typeof window === 'undefined') return;
  escuchando = true;
  window.addEventListener(
    'pointermove',
    (e) => {
      cursor.x = e.clientX;
      cursor.y = e.clientY;
      cursor.en = Date.now();
    },
    { passive: true },
  );
}
const quieto = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
let semillas = 0;

const C = {
  claro: 'oklch(.97 .03 var(--h))',
  medio: 'oklch(.8 .1 var(--h))',
  oscuro: 'oklch(.56 .16 var(--h))',
  ojos: 'oklch(.2 .05 var(--h))',
  onda: 'oklch(.58 .15 var(--h))',
};

export function Amix({ estado = 'reposo', size = 40, mira = false, className, titulo = 'Amix' }: { estado?: EstadoAmix; size?: number; mira?: boolean; className?: string; titulo?: string }) {
  const id = useId().replace(/:/g, '');
  const svg = useRef<SVGSVGElement>(null);
  const vivo = useRef({ estado, t0: 0, desde: NEUTRO as Gesto, ultimo: NEUTRO as Gesto, semilla: 0, mx: 0, my: 0, peso: 0 });

  // Cambio de estado: el nuevo guion arranca desde el gesto en que iba.
  useEffect(() => {
    const v = vivo.current;
    if (v.estado === estado && v.t0) return;
    v.desde = v.ultimo;
    v.estado = estado;
    v.t0 = performance.now();
  }, [estado]);

  useEffect(() => {
    const v = vivo.current;
    v.semilla = v.semilla || ++semillas * 131 + 17;
    if (!v.t0) v.t0 = performance.now();
    if (mira) seguirCursor();
    const s = svg.current;
    if (!s) return;
    const el = {
      todo: s.querySelector<SVGGElement>('[data-a="todo"]')!,
      sombra: s.querySelector<SVGEllipseElement>('[data-a="sombra"]')!,
      ondas: [...s.querySelectorAll<SVGPathElement>('[data-a="onda"]')],
      ojos: [0, 1].map((i) => ({
        cap: s.querySelector<SVGRectElement>(`[data-o="${i}"] rect`)!,
        arco: s.querySelector<SVGPathElement>(`[data-o="${i}"] [data-a="arco"]`)!,
        ceja: s.querySelector<SVGPathElement>(`[data-o="${i}"] [data-a="ceja"]`)!,
        luz: s.querySelector<SVGCircleElement>(`[data-o="${i}"] circle`)!,
      })),
    };
    const sinMover = quieto();
    let raf = 0;
    const cuadro = (ahora: number) => {
      const guion = GUIONES[v.estado];
      let ge = sinMover ? guion.pasos[0].gesto : gestoEn(guion, ahora - v.t0, v.desde);
      v.ultimo = ge;
      if (mira && !sinMover) {
        // Te mira mientras mueves el mouse; menos cuando trabaja y nada cuando espera tu respuesta.
        const peso = v.estado === 'esperando' ? 0 : v.estado === 'buscando' || v.estado === 'trabajando' ? 0.2 : 0.65;
        v.peso += ((Date.now() - cursor.en < 4000 ? peso : 0) - v.peso) * 0.08;
        const r = s.getBoundingClientRect();
        const dx = Math.max(-1, Math.min(1, (cursor.x - (r.left + r.width / 2)) / 320));
        const dy = Math.max(-1, Math.min(1, (cursor.y - (r.top + r.height / 2)) / 320));
        v.mx += (dx * 0.6 - v.mx) * 0.18;
        v.my += (dy * 0.6 - v.my) * 0.18;
        ge = { ...ge, mx: ge.mx + (v.mx - ge.mx) * v.peso, my: ge.my + (v.my - ge.my) * v.peso };
      }
      const p = pose(ge, ahora, v.semilla, sinMover);
      el.todo.setAttribute('transform', p.todo);
      el.sombra.setAttribute('rx', String(p.sombraRx));
      el.sombra.setAttribute('opacity', String(p.sombraOp));
      el.ondas.forEach((o) => o.setAttribute('opacity', String(p.ondas[Number(o.dataset.i)])));
      p.ojos.forEach((o, i) => {
        const e = el.ojos[i];
        e.cap.setAttribute('x', String(o.x - o.ancho / 2));
        e.cap.setAttribute('y', String(o.y - o.alto / 2));
        e.cap.setAttribute('width', String(o.ancho));
        e.cap.setAttribute('height', String(o.alto));
        e.cap.setAttribute('rx', String(Math.min(o.ancho, o.alto) / 2));
        e.cap.setAttribute('opacity', String(o.abierto));
        e.arco.setAttribute('d', o.arco);
        e.arco.setAttribute('opacity', String(1 - o.abierto));
        e.ceja.setAttribute('d', o.ceja);
        e.ceja.setAttribute('opacity', String(o.cejaOp));
        e.luz.setAttribute('cx', String(o.luzX));
        e.luz.setAttribute('cy', String(o.luzY));
        e.luz.setAttribute('opacity', o.abierto > 0.5 && o.alto > 3 ? '1' : '0');
      });
      if (!sinMover) raf = requestAnimationFrame(cuadro);
    };
    raf = requestAnimationFrame(cuadro);
    // Con movimiento reducido se dibuja una vez por estado.
    return () => cancelAnimationFrame(raf);
  }, [mira, estado]);

  return (
    <svg ref={svg} className={className} width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={titulo}>
      <defs>
        <radialGradient id={`${id}g`} cx="40%" cy="32%" r="75%">
          <stop offset="0" style={{ stopColor: C.claro }} />
          <stop offset=".55" style={{ stopColor: C.medio }} />
          <stop offset="1" style={{ stopColor: C.oscuro }} />
        </radialGradient>
      </defs>
      <ellipse data-a="sombra" cx="50" cy={BASE + 6} rx="22" ry="3.6" fill="#0f0d24" opacity=".18" />
      <g data-a="todo">
        {[0, 1, 2].map((i) => (
          <g key={i}>
            <path data-a="onda" data-i={i} d={`M${14 - i * 5} ${40 - i * 3}Q${8 - i * 5} 50 ${14 - i * 5} ${60 + i * 3}`} fill="none" strokeWidth="2.6" strokeLinecap="round" style={{ stroke: C.onda }} opacity="0" />
            <path data-a="onda" data-i={i} d={`M${86 + i * 5} ${40 - i * 3}Q${92 + i * 5} 50 ${86 + i * 5} ${60 + i * 3}`} fill="none" strokeWidth="2.6" strokeLinecap="round" style={{ stroke: C.onda }} opacity="0" />
          </g>
        ))}
        <path d={CUERPO} fill={`url(#${id}g)`} />
        <path d={CUERPO} fill="none" stroke="#fff" strokeOpacity=".55" strokeWidth="1.2" />
        {[0, 1].map((i) => (
          <g key={i} data-o={i}>
            <rect style={{ fill: C.ojos }} />
            <path data-a="arco" fill="none" strokeWidth="3.2" strokeLinecap="round" style={{ stroke: C.ojos }} opacity="0" />
            <path data-a="ceja" fill="none" strokeWidth="2.2" strokeLinecap="round" style={{ stroke: C.ojos }} opacity="0" />
            <circle r="1.25" fill="#fff" />
          </g>
        ))}
      </g>
    </svg>
  );
}
